import React, { createContext, useEffect, useReducer, useState } from 'react'
import reducer from './reducer'
import { appLocalStorage } from 'utils/misc'
import {
  DOWNLOAD_MANAGER_DOWNLOADS_KEY,
  DOWNLOAD_MANAGER_INITIAL_STATE,
  DOWNLOAD_MANAGER_PENDING_STATUS,
  DOWNLOAD_MANAGER_READY_STATUS,
  DOWNLOAD_MANAGER_STORAGE
} from './constants'
import { IDownload, IDownloadManagerContext, IDownloadManagerProvider } from './interface'
import { LOCAL_STORAGE_DOWNLOAD_MANAGER_KEY } from 'utils/constants'

export const DownloadManagerContext = createContext<IDownloadManagerContext>({
  manager: {},
  downloads: [],
  managerActive: false,
  updateManagerActive: () => {},
  activateService: () => {},
  deactivateService: () => {},
  isActiveService: () => false,
  updateDownload: () => {},
  findDownload: () => undefined,
  pendingDownloads: () => [],
  readyDownloads: () => [],
  filteredDownloads: () => [],
  updateDownloads: () => {}
})

const useDownloadManagerContext = (): IDownloadManagerContext => {
  const manager_data = DOWNLOAD_MANAGER_STORAGE || DOWNLOAD_MANAGER_INITIAL_STATE
  const [manager, setManager] = useReducer(reducer, manager_data)
  const [downloads, setDownloads] = useState<IDownload[]>(manager_data.downloads)
  const [managerActive, setManagerActive] = useState<boolean>(false)
  const [servicesActive, setServicesActive] = useState<string[]>([])

  useEffect(() => {
    appLocalStorage.setValue(LOCAL_STORAGE_DOWNLOAD_MANAGER_KEY, manager)
  }, [manager])

  useEffect(() => {
    setManager({ [DOWNLOAD_MANAGER_DOWNLOADS_KEY]: downloads })
  }, [downloads])

  const updateManagerActive = (status: boolean, service?: string): void => {
    if (service) {
      activateService(service)
    }

    setManagerActive(status)
  }

  const activateService = (service: string): void => setServicesActive([...servicesActive, service])

  const deactivateService = (service: string): void =>
    setServicesActive(servicesActive.filter(serviceActive => serviceActive !== service))

  const isActiveService = (service: string): boolean => servicesActive.includes(service)

  const updateDownload = (download: IDownload): void => {
    setDownloads(previousDownloads => {
      const { id, ...rest } = download
      const index_to_update = previousDownloads.findIndex((previousDownload: IDownload) => previousDownload.id === id)

      if (index_to_update >= 0) {
        const download_to_update = { ...previousDownloads[index_to_update], ...rest }
        const new_downloads = [
          ...previousDownloads.slice(0, index_to_update),
          download_to_update,
          ...previousDownloads.slice(index_to_update + 1)
        ]

        return new_downloads
      } else {
        return previousDownloads
      }
    })
  }

  const findDownload = (id: string): IDownload | undefined => {
    return downloads.find(download => download.id === id)
  }

  const pendingDownloads = (): IDownload[] => {
    return filteredDownloads({ status: DOWNLOAD_MANAGER_PENDING_STATUS })
  }

  const readyDownloads = (): IDownload[] => {
    return filteredDownloads({ status: DOWNLOAD_MANAGER_READY_STATUS })
  }

  const filteredDownloads = (data: Record<string, string | boolean>): IDownload[] => {
    const keys = Object.keys(data)
    return downloads.filter((download: IDownload) => keys.every(key => download[key as keyof IDownload] === data[key]))
  }

  const updateDownloads = (newDownloads: IDownload[]): void => {
    setDownloads(newDownloads)
  }

  return {
    manager,
    downloads,
    managerActive,
    updateManagerActive,
    activateService,
    deactivateService,
    isActiveService,
    updateDownload,
    findDownload,
    pendingDownloads,
    readyDownloads,
    filteredDownloads,
    updateDownloads
  }
}

export const DownloadManagerProvider = ({ children }: IDownloadManagerProvider): JSX.Element => {
  return (
    <DownloadManagerContext.Provider value={useDownloadManagerContext()}>{children}</DownloadManagerContext.Provider>
  )
}
